<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <div class="activity-container" slot="content">
        <div
          v-for="(round, roundIndex) in rounds"
          class="round-container"
          :id="[`round-container-${roundIndex}`]"
          :track-by="roundIndex"
          :key="roundIndex"
        >
          <div
            v-for="(step, stepIndex) in round.steps"
            :class="
              `${
                !step.activityImage && !step.activityAudio ? 'tab-box' : 'step-box'
              } round-${roundIndex}-step-${stepIndex}`
            "
            :id="[`round-${roundIndex}-step-${stepIndex}`]"
            :track-by="roundIndex"
            :key="`${stepIndex}-${step.answer}`"
            v-if="round.steps[stepIndex - 1]?.column !== step.column"
            aria-hidden="true"
          >
            <div class="image-container">
              <img
                :alt="step.activityImage?.image?.description"
                :src="step.activityImage?.image?.src"
                class="image"
              />
            </div>
            <div class="answer-container">
              <div class="answer" tabindex="0">
                <blinking-cursor-text
                  :roundId="roundIndex"
                  :text-step="stepIndex"
                  :text="step.answer"
                  :shouldBeCompletedStep="
                    roundIndex < progress.roundIndex || stepIndex < progress.stepIndex
                  "
                >
                </blinking-cursor-text>
              </div>
              <div class="tab">
                <div class="svg-container">
                  <svg
                    aria-label="Tab key"
                    class="svg-elem"
                    height="67px"
                    role="img"
                    width="101px"
                    v-html="tabKeySVG"
                  ></svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import AccessibilityHelper from 'mixins/AccessibilityHelper';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import tabKeySVG from 'assets/svg/keyboards/k2-keys/KeyTab.svg';

export default {
  name: 'tab-youre-it',
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper, AccessibilityHelper],

  components: {
    ActivityStage,
    BlinkingCursorText
  },

  data() {
    return {
      activityName: "Tab, You're It!",
      tabKeySVG
    };
  },

  methods: {
    async loadActivity() {
      await this.loadImages();
      this.sfx = await this.loadSounds();
      this.createRounds();
    },

    async loadImages() {
      this.activityData.rounds.forEach(({ steps }) =>
        steps.forEach(({ activityImage }) => this.queueImage(activityImage?.image?.src))
      );

      return this.downloadImages();
    },

    async loadSounds() {
      this.activityData.rounds.forEach(({ steps }) => {
        steps.forEach(({ activityAudio }) => {
          if (!activityAudio) return;
          this.queueAudio(activityAudio?.audio?.src);
        });
      });

      const cache = await this.downloadAudio();

      return this.rounds.map(({ steps }) =>
        steps.map(({ activityAudio }) => cache.get(activityAudio?.audio?.src))
      );
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;

      return {
        keyboardType,
        roofs,
        keyCodesToNotHandleNatively: [9]
      };
    },

    fadeInActivity() {
      const elem = document.getElementsByClassName('round-0-step-0');
      elem?.item(0)?.setAttribute('aria-hidden', 'false');

      TweenMax.to('.round-0-step-0', 0.5, { opacity: 1 });
    },

    getSvg(stepIndex) {
      const { roundIndex } = this.progress;
      return document.querySelectorAll(`.round-${roundIndex}-step-${stepIndex} .svg-elem`)[0];
    },

    fadeOutTab() {
      const { stepIndex } = this.progress;

      const isSecondTab =
        stepIndex > 0 &&
        !this.currentRound.steps[stepIndex - 1]?.activityImage &&
        !this.currentRound.steps[stepIndex - 1]?.activityAudio;
      const stepIndexToUse = isSecondTab ? this.progress.stepIndex : this.progress.stepIndex - 1;
      const svgElement = this.getSvg(stepIndexToUse);

      svgElement.setAttribute('aria-hidden', 'true');
      TweenMax.to(svgElement, 0.25, { opacity: 0 });
    },

    fadeInNextTab() {
      const isSecondTab = !this.currentStep?.activityImage && !this.currentStep?.activityAudio;
      const stepIndex = isSecondTab ? this.progress.stepIndex + 1 : this.progress.stepIndex;
      const svgElement = this.getSvg(stepIndex);

      svgElement.setAttribute('aria-hidden', 'false');
      TweenMax.to(svgElement, 1, { opacity: 1 });
    },

    fadeInNextStep() {
      const { roundIndex, stepIndex } = this.progress;

      const elem = document.getElementsByClassName(`round-${roundIndex}-step-${stepIndex + 1}`);
      elem?.item(0)?.setAttribute('aria-hidden', 'false');

      TweenMax.to(`.round-${roundIndex}-step-${stepIndex + 1}`, 1, { opacity: 1 });
    },

    fadeOutImages(tl) {
      const { roundIndex } = this.progress;
      const roundImages = document.querySelectorAll(`#round-container-${roundIndex} .image`);

      roundImages.forEach(image => {
        image.setAttribute('aria-hidden', 'true');
      });
      tl.staggerTo(roundImages, 0.25, { opacity: 0 }, 0.1);
    },

    moveContainer(tl) {
      const numberOfRounds = 2;
      const { roundIndex } = this.progress;

      Array.from(Array(roundIndex + 1).keys()).forEach(i => {
        const bottomY = `${(numberOfRounds - i) * 47}px`;
        tl.to(`#round-container-${i}`, 1, { y: bottomY });
      });
    },

    fadeInNextRound(tl) {
      const { roundIndex } = this.progress;

      const elem = document.getElementsByClassName(`round-${roundIndex + 1}-step-0`);
      elem?.item(0)?.setAttribute('aria-hidden', 'false');

      tl.to(`.round-${roundIndex + 1}-step-0`, 1, { opacity: 1 });
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.$nextTick(this.setActivityContainerHeight);
      this.$nextTick(this.fadeInActivity);
    },

    endActivity(tl) {
      tl.call(this.completeActivity);
    },

    handleRoundEnd() {
      const { roundIndex } = this.progress;

      // chain TimelineLite calls
      const tl = new TimelineLite();
      this.fadeOutImages(tl);
      if (roundIndex !== 2) {
        this.moveContainer(tl);
      }
      this.fadeInNextRound(tl);
      tl.call(this.setupNewRound);
      if (roundIndex === 2) {
        this.endActivity(tl);
      }
    },

    handleCorrectKey(attempt) {
      const { character } = attempt;
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      const { stepIndex } = this.progress;
      const isTab = character === 'tab';

      // skip if typing is not done for current word
      if (!this.isEndOfCurrentSentence) return;

      if (isTab) {
        this.fadeOutTab();
      } else {
        this.sndBell();
        this.sfx[this.progress.roundIndex][this.progress.stepIndex]?.play();
      }

      const fadeStep =
        this.currentRound?.steps?.[stepIndex + 1]?.activityAudio ||
        this.currentRound?.steps?.[stepIndex + 1]?.activityImage;

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      } else if (fadeStep) {
        this.fadeInNextStep();
      } else {
        this.rerenderScreenReaderAlert('Press the tab key again.');
        this.fadeInNextTab();
      }

      this.completeStep();
      this.setCurrentStep();
    }
  },

  watch: {
    expectedKey(newVal) {
      const { roundIndex, stepIndex } = this.progress;
      const word = this.rounds[roundIndex]?.steps?.[stepIndex]?.answer;

      if (!word) return;

      if (newVal !== 'tab') {
        const isMultipleWords = word.split(' ').length > 1;
        const instructions = `Type the word${isMultipleWords ? 's' : ''}  - ${word}`;
        if (instructions === this.currentScreenReaderAlert) {
          return;
        }

        this.rerenderScreenReaderAlert(instructions);
        return;
      }

      this.rerenderScreenReaderAlert('Press the tab key');
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-container {
  height: 100%;
  position: relative;
}

.answer-container {
  display: flex;
  height: 67px;
}

.tab,
.answer,
.svg-container {
  display: inline-block;
}

.svg-elem {
  background-color: #fff6cb;
  opacity: 0;
}

.tab,
.answer {
  vertical-align: top;
  height: 67px;
}

.answer {
  align-items: center;
  display: flex;
  max-width: 240px;
  width: 100%;
}

.is-chrome-ipad {
  .activity-container {
    .round-container:last-child {
      margin-top: -10px;
    }
  }
}

.image-container {
  min-width: 242px;
  min-height: 173px;

  img {
    max-width: 240px;
  }
}

.step-box {
  opacity: 0;
  margin-right: 15px;
  display: inline-block;
  font-size: 25px;
  vertical-align: top;
}

.tab-box {
  width: max-content;
  display: inline-block;
  font-size: 25px;
  vertical-align: top;

  .image-container {
    min-width: 10px;
    height: 183px;
  }

  .answer {
    display: none;
  }
}

#second-tab {
  margin-left: 0px;
  width: 200px;
}

.round-container {
  position: absolute;
  left: 20px;
  right: 40px;
  display: flex;
  justify-content: space-around;

  .step-box:last-child {
    margin-right: 0;
  }
}
</style>
